import type { PDFPageProxy } from 'pdfjs-dist'

import {
  acquireDocument,
  cappedPixelRatio,
} from '@/modules/projects/components/dialogs/pdfDocuments'
import type { PageSource } from './types'

// The strip never draws a card wider than this, so a thumbnail never needs more pixels.
export const THUMBNAIL_WIDTH = 216
const THUMBNAIL_QUALITY = 0.8
const CACHE_LIMIT = 400
const RENDERS_AT_ONCE = 2

const keyOf = (source: PageSource) => `${source.fileUrl}#${source.pageInFile}`

// One cache for the whole app. Object URLs outlive a remount, so leaving the view and coming
// back costs nothing, and a page is never rendered twice.
const thumbnails = new Map<string, string>()
// Every key we have already taken on: queued, rendering, drawn or failed. A page that failed
// stays here, which is what stops a broken file being retried forever.
const claimed = new Set<string>()
const waiting: PageSource[] = []
const listeners = new Set<() => void>()
let rendering = 0

// The document is held only while the page is drawn. The shared cache keeps a few open after
// that, so the next page of the same file does not fetch and parse it again.
const renderThumbnail = async (source: PageSource) => {
  const held = acquireDocument(source.fileUrl)
  try {
    const pdf = await held.promise
    return await drawThumbnail(await pdf.getPage(source.pageInFile))
  } finally {
    held.release()
  }
}

const drawThumbnail = async (page: PDFPageProxy) => {
  try {
    const unscaled = page.getViewport({ scale: 1 })
    const dpr = cappedPixelRatio()
    const viewport = page.getViewport({
      scale: (THUMBNAIL_WIDTH / unscaled.width) * dpr,
    })
    const canvas = document.createElement('canvas')
    canvas.width = viewport.width
    canvas.height = viewport.height

    await page.render({ canvas, viewport }).promise
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', THUMBNAIL_QUALITY),
    )
    if (!blob) throw new Error('The page canvas produced no image.')
    return URL.createObjectURL(blob)
  } finally {
    page.cleanup()
  }
}

const keep = (key: string, src: string) => {
  thumbnails.set(key, src)
  while (thumbnails.size > CACHE_LIMIT) {
    const oldest = thumbnails.keys().next().value
    if (oldest === undefined) return
    const dropped = thumbnails.get(oldest)
    if (dropped) URL.revokeObjectURL(dropped)
    thumbnails.delete(oldest)
    // Let an evicted page be rendered again if the user scrolls back to it.
    claimed.delete(oldest)
  }
}

const announce = () => {
  for (const listener of listeners) listener()
}

const pump = () => {
  while (rendering < RENDERS_AT_ONCE) {
    const next = waiting.shift()
    if (!next) return
    rendering += 1
    renderThumbnail(next)
      .then((src) => keep(keyOf(next), src))
      .catch(() => {
        // A page we cannot draw stays a blank sheet; retrying it would spin forever.
      })
      .finally(() => {
        rendering -= 1
        announce()
        pump()
      })
  }
}

export const requestPageImages = (sources: PageSource[]) => {
  const fresh = sources.filter((source) => !claimed.has(keyOf(source)))
  if (fresh.length === 0) return
  for (const source of fresh) claimed.add(keyOf(source))
  // Newest batch first: where the strip came to rest beats what it scrolled past.
  waiting.unshift(...fresh)
  pump()
}

export const hasPageImage = (source: PageSource) =>
  thumbnails.has(keyOf(source))

export const pageImageSrc = (source: PageSource) => {
  const key = keyOf(source)
  const src = thumbnails.get(key)
  // Re-insert so the first key in the Map is always the least recently used one.
  if (src !== undefined) {
    thumbnails.delete(key)
    thumbnails.set(key, src)
  }
  return src
}

export const subscribeToPageImages = (listener: () => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}
